import React from 'react';
import { Control, Controller, FieldErrors, UseFormHandleSubmit } from 'react-hook-form';
import { Button, Input } from 'antd';
import { EyeOutlined } from '@ant-design/icons';
import { LoginPayload } from '@/domains/user/ports/payloads';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

interface LoginFormProps {
  control: Control<LoginPayload>;
  handleSubmit: UseFormHandleSubmit<LoginPayload>;
  onSubmit: (data: LoginPayload) => Promise<void>;
  errors: FieldErrors<LoginPayload>;
  isLoggingIn: boolean;
  buttonStyle: React.CSSProperties;
  inputStyle: React.CSSProperties;
  onUsePasswordless?: () => void;
}

const LoginForm: React.FC<LoginFormProps> = ({
  control,
  handleSubmit,
  onSubmit,
  errors,
  isLoggingIn,
  buttonStyle,
  inputStyle,
  onUsePasswordless,
}) => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  return (
    <form onSubmit={handleSubmit(onSubmit)} className='space-y-6'>
      <div className='mb-6'>
        <label className='block text-sm font-medium text-gray-700 mb-1'>
          {t('common.usernameOrEmail')}(*)
        </label>
        <Controller
          name='email'
          control={control}
          render={({ field }) => (
            <Input {...field} style={inputStyle} placeholder={t('common.enterEmailOrUsername')} />
          )}
        />
        {errors.email && <p className='text-red-500 text-sm mt-1'>{errors.email.message}</p>}
      </div>
      <div className='mb-6'>
        <label className='block text-sm font-medium text-gray-700 mb-1'>
          {t('common.password')}(*)
        </label>
        <Controller
          name='password'
          control={control}
          render={({ field }) => (
            <Input.Password
              {...field}
              style={inputStyle}
              placeholder={t('common.enterPassword')}
              iconRender={(visible) => (visible ? <EyeOutlined /> : <EyeOutlined />)}
            />
          )}
        />
        {errors.password && <p className='text-red-500 text-sm mt-1'>{errors.password.message}</p>}
      </div>
      <Button
        type='primary'
        htmlType='submit'
        className='w-full'
        style={buttonStyle}
        loading={isLoggingIn}
      >
        {t('common.login')}
      </Button>
      <div className='text-center mt-4 space-y-2'>
        {onUsePasswordless && (
          <div>
            <button
              type='button'
              className='text-blue-500 text-sm bg-transparent border-none cursor-pointer'
              onClick={onUsePasswordless}
            >
              {t('common.loginWithEmailSmsCode')}
            </button>
          </div>
        )}
        <a href='#' className='text-blue-500 text-sm' onClick={() => navigate('/forgot-password')}>
          {t('common.clickHereIfYouForgotYourPassword')}
        </a>
      </div>
    </form>
  );
};

export default LoginForm;
